// React
import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';

// React Router
import { useNavigate, useParams } from 'react-router-dom';

// CoreUI
import { cilChevronBottom } from '@coreui/icons';
import { CFormCheck, CRow } from '@coreui/react';

// FontAwesome
import { faFileContract } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Custom Modals
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import DAContractModal from 'src/components/Modals/DAContractModal';
import EmailBrandNewModal from 'src/components/Modals/EmailBrandNewModal';
import TermsAndConditionModal from 'src/components/Modals/TermsAndConditionModal';

// Input Configs
import {
  getDaApplyBasicInputs,
  getDaApplyLicenseInputs,
  getDaApplySsnInputs,
  getInitialDaData,
  getInitialDaIdentificationData,
} from 'src/configs/InputConfig/addDAFormConfig';

// Helpers
import { formValidator } from 'src/helpers/formValidator';
import { generateRandomPassword } from 'src/helpers/generatePassowrd';

// UI
import { useSelector } from 'react-redux';
import DAAttachment from 'src/components/Component/DAAttachment';
import DataGridHeader from 'src/components/DataGridComponents/DataGridHeader';
import Inputs from 'src/components/Filters/Inputs';
import AppContainer from 'src/components/UI/AppContainer';
import Button from 'src/components/UI/Button';
import Form from 'src/components/UI/Form';
import Loading from 'src/components/UI/Loading';

// Hooks
import moment from 'moment';
import { useCreateBackgroundCheck, useGetBackgroundCheck } from 'src/api/hooks/useBackgroundCheck';
import { useFetchDsps } from 'src/hooks/api/useFetchDsps';
import { useUpdateUser } from 'src/hooks/api/useUpdateUser';
import { useUploadAvatar } from 'src/hooks/api/useUploadAvatar';
import { useUserAvailability } from 'src/hooks/api/useUserAvailability';
import useEmailVerification from 'src/hooks/useEmailVerification';
import { useShowSnackbar } from 'src/hooks/useShowSnackbar';
import { generateOfferLetter } from 'src/reports/pdf/daOfferLetter';
import { useFetchDSPConfigs } from 'src/hooks/api/useFetchGlobalConfigs';
import UniformSizeModal from 'src/components/Modals/UniformSizeModal';

const MAX_WIDTH = 6000;
const MAX_HEIGHT = 6000;

const AddDA = () => {
  const navigate = useNavigate();
  const showSnackbar = useShowSnackbar();
  const { dsp } = useParams();

  // ── API hooks ──────────────────────────────────────────────────────────────
  const { checkEmailValidation } = useEmailVerification();
  const { loading: dspLoading, fetchDsp } = useFetchDsps();
  const { createUpdateUser, loading: createUpdateUserLoading } = useUpdateUser();
  const { checkUserAvailability } = useUserAvailability();
  const { uploadAvatar, uploadAttachments, loading: uploadAttachmentsLoading } = useUploadAvatar();
  const { createBackgroundCheck } = useCreateBackgroundCheck();
  const { getBackgroundCheck } = useGetBackgroundCheck();
  const { dspConfigsRes, fetchDSPConfig, dspConfigsLoading } = useFetchDSPConfigs();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [daApplyFormData, setDaApplyFormData] = useState(getInitialDaData());
  const [daIdentificationData, setDaIdentificationData] = useState(
    getInitialDaIdentificationData(),
  );
  const [attachmentRows, setAttachmentRows] = useState([
    {
      id: 9,
      docType: '',
      frontAttachment: null,
      frontAttachmentPreview: '',
      backAttachment: null,
      backAttachmentPreview: '',
    },
  ]);

  // ── Signature & contract state ─────────────────────────────────────────────
  const [signature, setSignature] = useState('');
  const [isShowContract, setIsShowContract] = useState(false);
  const [daContractJson, setDAContractJSON] = useState({
    drugProcedure: '1',
    prescription: 'yes',
    notificationPolicy: 'a',
    breakWaiver: 3,
    dtref: '',
    bcref: '',
    drtref: '',
  });

  // ── Collapsible section state (Basic + License + SSN open by default) ──────
  const [showLicense, setShowLicense] = useState(true);
  const [showSSN, setShowSSN] = useState(true);
  const [showOtherDocs, setShowOtherDocs] = useState(false);

  // ── Modal / UI state ───────────────────────────────────────────────────────
  const [isThisBrandnew, setIsThisBrandnew] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [termsmodalOpen, setTermsmodalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [emailReadonly, setEmailReadonly] = useState(true);
  const [emailMessage, setEmailMessage] = useState('Enter Valid Email Address');
  const [dspList, setDspList] = useState([]);
  const [uniformSizeModalOpen, setUniformSizeModalOpen] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const lastVerifiedEmailRef = useRef('');
  const lastVerificationStatusRef = useRef('');

  // Selected DSP for contract modal
  const selectedDsp = useMemo(
    () => dspList?.find((d) => d.id == daApplyFormData.dspid),
    [dspList, daApplyFormData.dspid],
  );
  const dspConfig = useMemo(() => {
    return dspConfigsRes?.data || null;
  }, [dspConfigsRes]);
  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDspList();
  }, []);

  useEffect(() => {
    if (dspList.length === 0 || !dsp) return;
    handleDAFormData({ target: { name: 'dspid', value: dsp } });
  }, [dspList, dsp]);

  // Sync attachmentRows into identification data for submission
  useEffect(() => {
    setDaIdentificationData((prev) => ({ ...prev, attachmentsDocs: attachmentRows }));
  }, [attachmentRows]);

  useEffect(() => {
    if (selectedDsp && selectedDsp?.id) {
      getDSPConfig(selectedDsp?.id);
    }
  }, [selectedDsp]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getDSPConfig = async (dspId) => await fetchDSPConfig({ dspid: dspId?.toString() });

  const fetchDspList = async () => {
    const dspData = await fetchDsp();
    setDspList(dspData);
  };

  const toggleUniformSizeModal = () => setUniformSizeModalOpen((prev) => !prev);

  const handleUniformSizeSelect = (sizeId) => {
    setDaApplyFormData((prev) => ({ ...prev, uniformSize: sizeId }));
  };

  const toggleIsShowContract = () => setIsShowContract((prev) => !prev);

  // ── Form data handler ──────────────────────────────────────────────────────
  const handleDAFormData = (event, label = '') => {
    // Avatar
    if (label === 'avatar') {
      setDaApplyFormData((prev) => ({ ...prev, avatar: event }));
      return;
    }
    // Mailing address — guard against email-like input
    if (label === 'mailAddress' || event?.target?.name === 'mailAddress') {
      const address = event?.target ? event.target.value : event;
      if (address.includes('@')) {
        showSnackbar('Mailing address cannot contain an email address', 'warning');
        setDaApplyFormData((prev) => ({ ...prev, mailAddress: '' }));
        return;
      }
      setDaApplyFormData((prev) => ({ ...prev, mailAddress: address }));
      return;
    }
    // Date fields (label is the field name, event is the date value)
    if (label !== '') {
      setDaApplyFormData((prev) => ({
        ...prev,
        [label]: moment(event).utc().format(),
      }));
      return;
    }
    const { name, value, type, files, checked } = event.target;
    if (type === 'file') {
      const file = files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.src = reader.result;
          img.onload = () => {
            if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
              showSnackbar('Image size should be less than 6000 x 6000', 'warning');
              return;
            }
            setDaApplyFormData((prev) => ({
              ...prev,
              [name]: { src: reader.result, name: file.name },
            }));
          };
        };
        reader.readAsDataURL(file);
      } else {
        setDaApplyFormData((prev) => ({ ...prev, [name]: null }));
      }
    } else {
      setDaApplyFormData((prev) => {
        const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
        // Clear license dates if license number is cleared
        if (name === 'licenseNo' && (!value || value.trim() === '')) {
          updated.licenseIssueDate = null;
          updated.licenseExpiryDate = null;
        }
        return updated;
      });
    }
  };

  // ── Identification data handler ────────────────────────────────────────────
  const handleDAIdentification = (event, label) => {
    if (label === 'idExpiryDate') {
      setDaIdentificationData((prev) => ({
        ...prev,
        [label]: moment(event).utc().format(),
      }));
      return;
    }

    const { name, value, files, type, id } = event.target;

    if (type === 'file') {
      const file = files[0];
      if (file) {
        // Store rawFile immediately so it's ready for FormData upload
        setDaIdentificationData((prev) => ({
          ...prev,
          [name]: {
            rawFile: file,
            name: file.name,
            type: file.type,
            src: null,
            previewLoading: true,
          },
        }));

        const reader = new FileReader();
        const isPDF = file.type === 'application/pdf';

        reader.onloadend = () => {
          setDaIdentificationData((prev) => {
            if (!prev[name]?.rawFile) return prev;
            if (isPDF) {
              return {
                ...prev,
                [name]: { ...prev[name], src: reader.result, previewLoading: false },
              };
            }
            // Image — validate dimensions asynchronously
            const img = new Image();
            img.src = reader.result;
            img.onload = () => {
              if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
                showSnackbar('Image size should be less than 6000 x 6000', 'warning');
                setDaIdentificationData((p) => ({ ...p, [name]: null }));
                const input = document.getElementById(id);
                if (input) input.value = '';
                return;
              }
              setDaIdentificationData((p) => ({
                ...p,
                [name]: { ...p[name], src: reader.result, previewLoading: false },
              }));
            };
            img.onerror = () => {
              showSnackbar('Invalid image file', 'error');
              setDaIdentificationData((p) => ({ ...p, [name]: null }));
              const input = document.getElementById(id);
              if (input) input.value = '';
            };
            return prev; // defer; img.onload will do the final setState
          });
        };
        reader.readAsDataURL(file);
      } else {
        setDaIdentificationData((prev) => ({ ...prev, [name]: null }));
      }
    } else {
      setDaIdentificationData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ── Email handlers ─────────────────────────────────────────────────────────
  const handleEmailChange = (e) => {
    const newEmail = e.target.value.trim();
    setDaApplyFormData((prev) => ({ ...prev, email: newEmail }));
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})?$/;
    // if (emailRegex.test(newEmail)) {
    //   setIsThisBrandnew(true);
    //   setModalOpen(true);
    // }
  };

  const onBlur = async () => {
    const fieldValue = daApplyFormData.email?.trim();
    const emailInputElement = document.getElementById('email');

    if (!fieldValue) {
      setEmailMessage('');
      emailInputElement.setCustomValidity('');
      lastVerifiedEmailRef.current = '';
      lastVerificationStatusRef.current = '';
      return;
    }
    if (
      lastVerifiedEmailRef.current === fieldValue &&
      ['valid', 'associated', 'processing'].includes(lastVerificationStatusRef.current)
    )
      return;

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})?$/;
    if (!emailRegex.test(fieldValue)) {
      const msg = `${fieldValue} is not a valid email`;
      showSnackbar(msg, 'error');
      setEmailMessage(msg);
      emailInputElement.setCustomValidity(msg);
      lastVerifiedEmailRef.current = fieldValue;
      lastVerificationStatusRef.current = 'invalid';
      return;
    }
    if (lastVerifiedEmailRef.current !== fieldValue) handleFocus();
  };

  const emailValidatorFn = async () => {
    const fieldValue = daApplyFormData.email?.trim();
    const emailInputElement = document.getElementById('email');

    emailInputElement.setCustomValidity('');
    lastVerifiedEmailRef.current = fieldValue;
    lastVerificationStatusRef.current = 'processing';

    const isValidEmail = await checkEmailValidation(fieldValue);
    if (isValidEmail !== null && isValidEmail !== 'valid') {
      const msg = `${fieldValue} is not a valid email`;
      showSnackbar(msg, 'error');
      setEmailMessage(msg);
      emailInputElement.setCustomValidity(msg);
      lastVerificationStatusRef.current = 'invalid';
      return;
    }

    const resultMsg = await new Promise((resolve) => {
      checkUserAvailability(fieldValue, '', daApplyFormData.id, (msg) => resolve(msg));
    });

    if (resultMsg && resultMsg.toLowerCase().includes('already associated')) {
      showSnackbar(resultMsg, 'warning');
      setEmailMessage(resultMsg);
      setDaApplyFormData((prev) => ({ ...prev, email: '' }));
      const el = document.getElementById('email');
      if (el) el.value = '';
      lastVerificationStatusRef.current = 'associated';
    } else {
      setEmailMessage(resultMsg || '');
      lastVerificationStatusRef.current = 'valid';
    }
  };

  // ── Background check ───────────────────────────────────────────────────────
  const createbgCheck = async (userData) => {
    await createBackgroundCheck(userData);
  };

  // ── Build FormData for file uploads ───────────────────────────────────────
  const createFormDataFromState = (identificationData, userId) => {
    const formData = new FormData();
    let hasFiles = false;

    // Fixed sr slots for standard documents
    const fixedFields = [
      { field: 'licenceImageFront', sr: 1 },
      { field: 'licenceImageBack', sr: 2 },
      { field: 'ssnFront', sr: 3 },
      { field: 'ssnBack', sr: 4 },
      { field: 'bCertificateFile', sr: 5 },
      { field: 'dotCert', sr: 6 },
    ];

    fixedFields.forEach(({ field, sr }) => {
      const fieldData = identificationData[field];
      if (fieldData?.rawFile instanceof File) {
        const file = fieldData.rawFile;
        const lastDot = file.name.lastIndexOf('.');
        const ext = lastDot !== -1 ? file.name.substring(lastDot + 1) : '';
        const customName = `${moment().valueOf()}>${sr}>0${ext ? `.${ext}` : ''}`;
        formData.append(sr.toString(), new File([file], customName, { type: file.type }));
        hasFiles = true;
      }
    });

    // Dynamic "Other Documents" rows start at sr 7
    let dynamicSr = 7;
    (identificationData.attachmentsDocs || []).forEach((row) => {
      ['front', 'back'].forEach((side) => {
        const attachment = row[`${side}Attachment`];
        if (attachment instanceof File) {
          const lastDot = attachment.name.lastIndexOf('.');
          const ext = lastDot !== -1 ? attachment.name.substring(lastDot + 1) : '';
          const customName = `${moment().valueOf()}>${dynamicSr}>${parseInt(row.docType) || 0}>0${
            ext ? `.${ext}` : ''
          }`;
          formData.append(
            dynamicSr.toString(),
            new File([attachment], customName, { type: attachment.type }),
          );
          hasFiles = true;
          dynamicSr++;
        }
      });
    });

    if (hasFiles) {
      formData.append('id', '0');
      formData.append('fileName', '');
      formData.append('daid', userId);
      formData.append('createdBy', userId || '');
      formData.append('dspid', daApplyFormData?.dspid || '');
      formData.append('lastUpdatedBy', userId || '');
      formData.append('createdAt', moment().utc().format());
      formData.append('lastUpdatedAt', moment().utc().format());
      formData.append('rowVer', 1);
    }

    return formData;
  };

  // ── Upload attachments + trigger background check ─────────────────────────
  const uploadDaAttachments = async (userId, userData) => {
    const formData = createFormDataFromState(daIdentificationData, userId);
    const isEmpty = [...formData.entries()].length === 0;

    if (!isEmpty) {
      const attachmentsRes = await uploadAttachments(formData);

      const bgRes = await getBackgroundCheck(userData);
      if (bgRes && bgRes.length === 0) await createbgCheck(userData);

      if (attachmentsRes?.status === true) {
        generateOfferLetter(
          dspList?.find((d) => d.id == daApplyFormData.dspid),
          daApplyFormData,
        );
        navigate('/');
      } else {
        showSnackbar(attachmentsRes?.message || 'File upload failed', 'error');
      }
    } else {
      const bgRes = await getBackgroundCheck(userData);
      if (bgRes && bgRes.length === 0) await createbgCheck(userData);
      generateOfferLetter(
        dspList?.find((d) => d.id == daApplyFormData.dspid),
        daApplyFormData,
      );
      navigate('/');
    }
  };
  // ── Submit ─────────────────────────────────────────────────────────────────
  const submitDA = async () => {
    try {
      formValidator();
      const form = document.querySelector('.apply-da-form');

      if (!form.checkValidity()) {
        // Expand critical sections so errors are visible
        setShowLicense(true);
        setShowSSN(true);
        return;
      }
      if (!daApplyFormData.dob) {
        showSnackbar('Please select Date of Birth', 'warning');
        return;
      }
      if (!daApplyFormData.hasValidDrivingLicense) {
        showSnackbar('Must confirm "Has Valid License"', 'warning');
        setShowLicense(true);
        return;
      }
      if (daApplyFormData.hasValidDrivingLicense) {
        if (!daApplyFormData.licenseIssueDate) {
          showSnackbar('Please select License Issue Date', 'warning');
          setShowLicense(true);
          return;
        }
        if (!daApplyFormData.licenseExpiryDate) {
          showSnackbar('Please select License Expiry Date', 'warning');
          setShowLicense(true);
          return;
        }
      }
      if (!daIdentificationData.licenceImageFront) {
        showSnackbar('Please attach License copy (front)', 'warning');
        setShowLicense(true);
        return;
      }
      if (!daIdentificationData.licenceImageBack) {
        showSnackbar('Please attach License copy (back)', 'warning');
        setShowLicense(true);
        return;
      }
      if (!daApplyFormData.isDOTCertified) {
        showSnackbar('Please select DOT certification (Yes/No)', 'warning');
        setShowLicense(true);
        return;
      }
      if (
        (daApplyFormData?.isDOTCertified === 1 || daApplyFormData?.isDOTCertified == true) &&
        !daIdentificationData?.dotCert
      ) {
        showSnackbar('Attach DOT certificate to proceed', 'warning');
        return;
      }
      if (
        (daApplyFormData?.isDOTCertified === 1 || daApplyFormData?.isDOTCertified == true) &&
        !daApplyFormData?.dotExpDate
      ) {
        showSnackbar('Please select DOT certificate expiry date', 'warning');
        return;
      }
      if (!daIdentificationData.ssnNo || daIdentificationData.ssnNo.trim() === '') {
        showSnackbar('SSN Number is required before submission', 'warning');
        setShowSSN(true);
        document.getElementById('ssnNo')?.focus();
        return;
      }
      if (!daIdentificationData.ssnFront) {
        showSnackbar('Please attach SSN copy (front)', 'warning');
        setShowSSN(true);
        return;
      }
      // ── Signature validation ───────────────────────────────────────────────
      if (!signature) {
        toggleIsShowContract();
        showSnackbar('Need to sign offer letter and documents', 'warning');
        return;
      }
      if (daApplyFormData.email === '') {
        setEmailReadonly(false);
        return;
      }

      const daBody = {
        ...daApplyFormData,
        sizeId: daApplyFormData.uniformSize ? Number(daApplyFormData.uniformSize) : 0,
        secondaryContact: daApplyFormData.isWhatsAppAsso ? daApplyFormData.primaryContact : '',
        address: daApplyFormData.mailAddress,
        dspName: dspList?.find((d) => d.id == daApplyFormData.dspid)?.name || '',
        userName: daApplyFormData.userName || daApplyFormData.email,
        password: daApplyFormData.password || generateRandomPassword(12),
        roleId: daApplyFormData.positionType ?? 0,
        stateId: daApplyFormData.issuingStateId,
        rowVer: 0,
        signature: signature,
        OfferTypeId: daApplyFormData.offerType,
        SuffixId: daApplyFormData.suffix,
        ssn: daIdentificationData.ssnNo,
        bcId: daIdentificationData.bCertificateNo,
        bcIssuingStateId: parseInt(daIdentificationData.bCertificateState) || 0,
        hasValidDrivingLicense: daApplyFormData.hasValidDrivingLicense ? 1 : 0,
        isDOTCertified:
          daApplyFormData.isDOTCertified === '' ? 0 : parseInt(daApplyFormData.isDOTCertified),
        dotExpDate: daApplyFormData.dotExpDate,
        remarks: 'created da',
        createdBy: 1,
        lastUpdatedBy: 1,
        lastUpdatedAt: moment().utc().format(),
        AdditionalInfoJson: JSON.stringify(daContractJson),
      };

      // Handle avatar upload if file was picked
      const fUpload = document.getElementById('fileAvatar');
      if (fUpload?.files?.length > 0) {
        const avatar = fUpload.files[0];
        const avatarForm = new FormData();
        avatarForm.append('file', avatar);
        avatarForm.append('id', '0');
        avatarForm.append('name', avatar.name);
        avatarForm.append('fileName', avatar.name);
        avatarForm.append('createdBy', '1');
        avatarForm.append('createdAt', moment().utc().format());

        const uploadAvatarRes = await uploadAvatar(avatarForm);
        if (uploadAvatarRes?.status === true) {
          const avatarPath =
            'productimages/' + uploadAvatarRes?.keyValue?.toString()?.split('\\').pop();
          const res = await createUpdateUser({ ...daBody, avatar: avatarPath });
          if (res?.status === true || res?.data?.id) {
            await uploadDaAttachments(res?.data?.id || res?.id, res?.data || res);
          } else {
            showSnackbar(res?.message || 'User creation failed', 'error');
          }
        } else {
          showSnackbar(uploadAvatarRes?.message || 'Avatar upload failed', 'error');
        }
      } else {
        const res = await createUpdateUser(daBody);
        if (res?.status === true || res?.data?.id) {
          await uploadDaAttachments(res?.data?.id || res?.id, res?.data || res);
        } else if (res?.message?.includes('sending mail.')) {
          await uploadDaAttachments(res?.data?.id || res?.id, res?.data || res);
          showSnackbar('Account created successfully, email sending failed', 'success');
        } else {
          showSnackbar(res?.message || 'User creation failed', 'error');
        }
      }
    } catch (error) {
      showSnackbar(error?.message || 'Unexpected error occurred', 'error');
    }
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const toggleModal = () => setModalOpen((p) => !p);
  const TermsModal = () => setTermsmodalOpen((p) => !p);
  const confirmationModal = () => setConfirmationModalOpen((p) => !p);
  const goToAnotherPage = () => startTransition(() => navigate('/'));
  const IsThisBrandNewClick = () => setIsThisBrandnew((p) => !p);
  const handleFocus = () => {
    setModalOpen(true);
    setIsThisBrandnew(true);
  };

  const associatedWithAmazonNo = () => {
    emailValidatorFn();
    toggleModal();
    setEmailReadonly(false);
  };

  const associatedWithAmazon = () => {
    const el = document.getElementById('email');
    if (el) el.value = '';
    setDaApplyFormData((prev) => ({ ...prev, email: '' }));
    lastVerifiedEmailRef.current = '';
    lastVerificationStatusRef.current = '';
    toggleModal();
    setEmailReadonly(true);
  };

  // ── Input configs per section ──────────────────────────────────────────────
  // Filter out the text signature field — signature is now handled via DAContractModal
  const basicInputs = getDaApplyBasicInputs(
    daApplyFormData,
    handleDAFormData,
    handleEmailChange,
    emailReadonly,
    emailMessage,
    onBlur,
    dspList,
    dsp,
    toggleUniformSizeModal, // ✅ new
  ).filter((input) => input.name !== 'signature');

  const licenseInputs = getDaApplyLicenseInputs(
    daApplyFormData,
    handleDAFormData,
    daIdentificationData,
    handleDAIdentification,
  );
  const ssnInputs = getDaApplySsnInputs(daIdentificationData, handleDAIdentification);

  const isLoading = dspLoading || createUpdateUserLoading || uploadAttachmentsLoading;
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <UniformSizeModal
        isOpen={uniformSizeModalOpen}
        toggle={toggleUniformSizeModal}
        selectedSize={Number(daApplyFormData.uniformSize)}
        onSelectSize={handleUniformSizeSelect}
        suffix={daApplyFormData.suffix} // ✅ for smart default tab
      />
      {/* ── DA Contract / Signature Modal ── */}
      <DAContractModal
        isOpen={isShowContract}
        toggle={toggleIsShowContract}
        signature={signature}
        setSignature={setSignature}
        setDAContractJSON={setDAContractJSON}
        daContractJson={daContractJson}
        selectedDsp={{
          ...selectedDsp,
          logo: selectedDsp?.logoPath || '',
          hourlyRate:
            daApplyFormData.positionType == 8
              ? dspConfig?.daHelperContactOfferRate
              : dspConfig?.daContactOfferRate,
        }}
        daApplyFormData={{
          ...daApplyFormData,
          positionType: daApplyFormData.positionType,
          address: daApplyFormData.mailAddress,
          stateId: daApplyFormData.issuingStateId,
        }}
        disable={false}
      />

      <>
        <DataGridHeader filterDisable={true} />

        <div style={{ textAlign: 'center' }}>
          <img
            className="LogoStyl mb-2"
            src="4DSPS_White.png"
            alt="logo"
            style={{ width: '150px', display: 'inline-block' }}
          />
        </div>

        <Form name="apply-da-form">
          {/* ══════════════════════════════════════════════
              SECTION 1 — Basic Info
          ══════════════════════════════════════════════ */}
          <AppContainer>
            <div className="show-stock">
              <Inputs inputFields={basicInputs} isBtn={false} />

              {/* ── Agreement Contract button (mirrors DAApplyForm) ── */}
              <div className="col-md-6">
                <div className="d-flex flex-column">
                  <label className="login_label labelName text-left mb-2 mt-3">
                    Agreement Contract
                  </label>
                  <Button
                    title={signature ? 'View and Download' : 'View and Sign'}
                    onClick={toggleIsShowContract}
                    className="btnsize"
                    loading={dspConfigsLoading}
                    icon={
                      <FontAwesomeIcon
                        icon={faFileContract}
                        size="xl"
                        style={{ marginRight: 10 }}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </AppContainer>

          {/* ══════════════════════════════════════════════
              SECTION 2 — License & DOT
          ══════════════════════════════════════════════ */}
          <AppContainer>
            <DataGridHeader
              title="License & DOT"
              otherControls={[{ icon: cilChevronBottom, fn: () => setShowLicense((p) => !p) }]}
              filterDisable={true}
            />
            {showLicense && (
              <div className="show-stock">
                <Inputs inputFields={licenseInputs} isBtn={false} />
              </div>
            )}
          </AppContainer>

          {/* ══════════════════════════════════════════════
              SECTION 3 — SSN
          ══════════════════════════════════════════════ */}
          <AppContainer>
            <DataGridHeader
              title="SSN"
              otherControls={[{ icon: cilChevronBottom, fn: () => setShowSSN((p) => !p) }]}
              filterDisable={true}
            />
            {showSSN && (
              <div className="show-stock">
                <label className="fs-6 fst-italic text-warning">
                  For smooth On Boarding Process SSN is required
                </label>
                <Inputs inputFields={ssnInputs} isBtn={false} />
              </div>
            )}
          </AppContainer>

          {/* ══════════════════════════════════════════════
              SECTION 4 — Other Documents
          ══════════════════════════════════════════════ */}
          <AppContainer>
            <DataGridHeader
              title="Other Documents"
              otherControls={[{ icon: cilChevronBottom, fn: () => setShowOtherDocs((p) => !p) }]}
              filterDisable={true}
            />
            {showOtherDocs && (
              <div className="show-stock">
                <DAAttachment
                  daIdentificationData={daIdentificationData}
                  setDAIdentificationData={setDaIdentificationData}
                  attachmentRows={attachmentRows}
                  setAttachmentRows={setAttachmentRows}
                />

                <div className="mb-0 dashboard-table mt-2">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-outline text-start">
                        <label htmlFor="additionalInfo" className="login_label labelName mb-2">
                          Additional Information
                        </label>
                        <textarea
                          value={daIdentificationData.additionalInfo}
                          onChange={handleDAIdentification}
                          className="FullWidth form-control"
                          rows="3"
                          name="additionalInfo"
                          id="additionalInfo"
                          placeholder="Additional information"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </AppContainer>

          {/* ══════════════════════════════════════════════
              Terms & Action Buttons — always visible
          ══════════════════════════════════════════════ */}
          <CFormCheck
            className="mt-3 d-flex flex-row justify-content-center"
            label={
              <span>
                By providing this info, you agree to our terms & conditions, read our{' '}
                <strong className="lblTerms" onClick={TermsModal}>
                  Terms & Conditions (EULA)
                </strong>
              </span>
            }
            name="isTermsAccepted"
            id="isTermsAccepted"
            required
            checked={daApplyFormData.isTermsAccepted}
            onChange={handleDAFormData}
          />

          <CRow className="CenterAlign gap-3 mb-2">
            <Button title="Cancel" onClick={confirmationModal} disabled={isLoading} />
            <Button
              title="Submit"
              type="submit"
              onClick={submitDA}
              disabled={isLoading}
              loading={createUpdateUserLoading}
            />
          </CRow>
        </Form>

        <ConfirmationModal
          header="Confirmation!"
          body="Are you sure you want to cancel?"
          isOpen={confirmationModalOpen}
          onYes={goToAnotherPage}
          onNo={confirmationModal}
        />
      </>

      <EmailBrandNewModal
        isOpen={modalOpen}
        toggle={toggleModal}
        isThisBrandnew={isThisBrandnew}
        IsThisBrandNewClick={IsThisBrandNewClick}
        associatedWithAmazonNo={associatedWithAmazonNo}
        associatedWithAmazon={associatedWithAmazon}
      />
      <TermsAndConditionModal isOpen={termsmodalOpen} toggle={TermsModal} />
    </React.Fragment>
  );
};

export default AddDA;
